import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, FileSearch, RefreshCw, Search } from 'lucide-react';
import api from '../api';
import ActionButton from '../components/ActionButton';
import AdminJobModal from '../components/AdminJobModal';
import ErrorAlert from '../components/ErrorAlert';
import {
  ACTIVE_STATUSES,
  EXPORTS,
  FILTERS,
  TERMINAL_STATUSES,
  aggregateReviewRows,
  downloadCsv,
  downloadJson,
  jobStatusLabel,
  rowIssueTypes,
  safeBulkActionForRow,
  suggestedCanonicalForRow,
} from './taxonomyLibrarian/taxonomyLibrarianUtils';
import useTaxonomyReviewActions from './taxonomyLibrarian/useTaxonomyReviewActions';
import TaxonomyReviewTable from './taxonomyLibrarian/TaxonomyReviewTable';

export default function TaxonomyCleanupView({ onOpenPlace }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [streamError, setStreamError] = useState('');
  const [runError, setRunError] = useState('');
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const reviewActions = useTaxonomyReviewActions({ setJob });
  const {
    cleanupNotice,
    cleanupError,
    setCleanupError,
    bulkApplying,
    bulkApplySafeRows,
    normalizingLegacy,
    normalizeLegacyBuckets,
    servicesSplitPass,
    closeServicesSplitPass,
    classifyServicesSplitCandidate,
    applyVisibleServicesSplitSuggestions,
    applyingServicesSuggestions,
    reviewingQueueId,
  } = reviewActions;

  const loadLatest = useCallback(async () => {
    setLoading(true);
    try {
      setJob(await api.getLatestTaxonomyLibrarianRun());
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  useEffect(() => {
    if (!job?.id || !ACTIVE_STATUSES.has(job.status)) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    let retryTimer = null;

    const connect = async () => {
      let reachedTerminal = false;
      try {
        await api.streamAdminJob(job.id, next => {
          reachedTerminal = TERMINAL_STATUSES.has(next.status);
          setJob(next);
          setStreamError('');
        }, { signal: controller.signal });
        if (!cancelled && !reachedTerminal) retryTimer = window.setTimeout(connect, 1500);
      } catch (err) {
        if (err.name === 'AbortError' || cancelled) return;
        setStreamError(err.message);
        retryTimer = window.setTimeout(connect, 1500);
      }
    };

    connect();
    return () => {
      cancelled = true;
      controller.abort();
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startReport = async () => {
    setJobModalOpen(true);
    setStarting(true);
    setRunError('');
    try {
      const next = await api.startTaxonomyLibrarianRun();
      setJob(next);
      setError('');
      setStreamError('');
    } catch (err) {
      setError(err.message);
      setRunError(err.message);
    } finally {
      setStarting(false);
    }
  };

  const result = job?.result || null;
  const reviewQueueRows = useMemo(() => aggregateReviewRows(result?.review_queue || []), [result]);
  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return reviewQueueRows.filter(row => {
      if (filter === 'high_priority' && Number(row.priority) > 30) return false;
      if (filter === 'alias_candidate' && row.review_type !== 'alias_candidate') return false;
      if (!['all', 'high_priority', 'alias_candidate'].includes(filter) && !rowIssueTypes(row).includes(filter)) return false;
      if (!query) return true;
      return [row.family, row.tag, row.raw_value, suggestedCanonicalForRow(row), row.candidate_tag, row.issue_type, row.all_issue_types, row.reason, row.recommended_action, row.place_master_id, row.card_key]
        .some(value => String(value || '').toLowerCase().includes(query));
    });
  }, [filter, reviewQueueRows, search]);

  const runExport = () => {
    if (!result) return;
    const suffix = filter === 'all' ? 'review_queue' : `review_queue_${filter}`;
    downloadCsv(suffix, rows, EXPORTS.review_queue.fields);
  };

  const downloadExport = name => {
    if (!result) return;
    const config = EXPORTS[name];
    const exportRows = name === 'review_queue' ? reviewQueueRows : (result[name] || []);
    downloadCsv(name, exportRows, config.fields);
  };

  if (loading) return <div className="flex h-52 items-center justify-center text-sm text-gray-500">Loading...</div>;

  const active = ACTIVE_STATUSES.has(job?.status);
  const progress = job?.progress || { current: 0, total: 1, message: '' };
  const progressPercent = Math.min(100, Math.round((Number(progress.current) / Math.max(Number(progress.total), 1)) * 100));
  const summary = result ? { ...(result.summary || {}), review_rows: reviewQueueRows.length } : {};
  const safeBulkRows = rows.filter(row => safeBulkActionForRow(row));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm">
          <span className={`h-2.5 w-2.5 rounded-full ${job?.status === 'failed' ? 'bg-red-500' : active ? 'bg-amber-500' : result ? 'bg-emerald-500' : 'bg-gray-300'}`} />
          <span className="font-medium text-gray-800">{jobStatusLabel(job)}</span>
          {result?.generated_at && (
            <span className="text-gray-500">{new Date(result.generated_at).toLocaleString()}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {result && (
            <>
              <select
                value={filter}
                onChange={event => setFilter(event.target.value)}
                aria-label="Proposal filter"
                className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700"
              >
                {FILTERS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
              <button
                type="button"
                onClick={runExport}
                aria-label="Download CSV"
                title="Download filtered proposals CSV"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
              >
                <Download className="h-4 w-4" />
              </button>
              <ActionButton
                icon={CheckCircle2}
                onClick={() => bulkApplySafeRows(rows)}
                loading={bulkApplying}
                disabled={active || bulkApplying || safeBulkRows.length === 0}
                size="sm"
              >
                Apply safe ({safeBulkRows.length})
              </ActionButton>
            </>
          )}
          <ActionButton icon={RefreshCw} onClick={normalizeLegacyBuckets} loading={normalizingLegacy} disabled={active || normalizingLegacy} size="sm">
            Normalize legacy buckets
          </ActionButton>
          <ActionButton icon={RefreshCw} onClick={startReport} loading={starting} disabled={active} size="sm">
            Run Librarian
          </ActionButton>
        </div>
      </div>

      {error && <ErrorAlert error={error} onClose={() => setError('')} />}
      {streamError && <ErrorAlert error={`Live updates reconnecting: ${streamError}`} onClose={() => setStreamError('')} />}
      {cleanupError && <ErrorAlert error={cleanupError} onClose={() => setCleanupError('')} />}
      {cleanupNotice && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {cleanupNotice}
        </div>
      )}

      {servicesSplitPass?.open && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-amber-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-amber-950">Services lane classification</p>
              <p className="mt-1 text-xs text-amber-800">
                Choose whether each affected card is guest-practical for Companion or local-directory only. Each click saves immediately; lane changes remove stale live projections until the card is republished.
              </p>
              <p className="mt-1 text-xs text-amber-700">
                Remaining: {Number(servicesSplitPass.count || 0).toLocaleString()} · Already classified: {Number(servicesSplitPass.already_classified_count || 0).toLocaleString()}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={applyVisibleServicesSplitSuggestions}
                disabled={
                  applyingServicesSuggestions
                  || servicesSplitPass.loading
                  || !(servicesSplitPass.candidates || []).some(candidate => (
                    candidate.suggested_target_lane
                    && candidate.current_retrieval_lane !== candidate.suggested_target_lane
                  ))
                }
                className="rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                {applyingServicesSuggestions ? 'Applying...' : 'Apply visible suggestions'}
              </button>
              <button
                type="button"
                onClick={closeServicesSplitPass}
                className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              >
                Close
              </button>
            </div>
          </div>
          {servicesSplitPass.loading && (
            <p className="px-4 py-4 text-sm text-amber-800">Loading affected cards...</p>
          )}
          {servicesSplitPass.error && (
            <p className="px-4 py-4 text-sm text-red-700">{servicesSplitPass.error}</p>
          )}
          {!servicesSplitPass.loading && !servicesSplitPass.error && (
            <div className="divide-y divide-amber-200/80">
              {servicesSplitPass.candidates.length === 0 && (
                <p className="px-4 py-4 text-sm text-amber-800">No remaining active cards need lane classification for this services row.</p>
              )}
              {servicesSplitPass.candidates.map(candidate => {
                const busy = reviewingQueueId === servicesSplitPass.row?.queue_id;
                return (
                  <div key={`${candidate.place_master_id}:${candidate.card_key}`} className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_auto]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-gray-900">{candidate.place_name}</p>
                        <span className="rounded border border-amber-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-amber-800">{candidate.card_key}</span>
                        <span className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-600">{candidate.current_retrieval_lane || 'unrouted'}</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-600">{candidate.card_title || 'Untitled card'} · {candidate.area || 'Unknown area'}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        Place: {candidate.place_sector_primary || '-'} / {candidate.place_category_type || '-'} · Card: {candidate.card_category || candidate.card_sector || '-'}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        {candidate.suggested_target_lane ? (
                          <>
                            <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700">
                              Suggested: {candidate.suggested_target_lane}
                            </span>
                            <span className="text-gray-600">{candidate.suggestion_reason}</span>
                          </>
                        ) : (
                          <span className="rounded border border-amber-200 bg-white px-1.5 py-0.5 font-medium text-amber-800">
                            Needs manual lane review
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:justify-end">
                      {candidate.suggested_target_lane && (
                        <button
                          type="button"
                          onClick={() => classifyServicesSplitCandidate(candidate, candidate.suggested_target_lane)}
                          disabled={busy || candidate.current_retrieval_lane === candidate.suggested_target_lane}
                          className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                        >
                          Apply suggested
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => classifyServicesSplitCandidate(candidate, 'companion_practical')}
                        disabled={busy || candidate.current_retrieval_lane === 'companion_practical'}
                        className="rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        Guest-practical
                      </button>
                      <button
                        type="button"
                        onClick={() => classifyServicesSplitCandidate(candidate, 'local_directory')}
                        disabled={busy || candidate.current_retrieval_lane === 'local_directory'}
                        className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Local-directory
                      </button>
                      <button
                        type="button"
                        onClick={() => onOpenPlace?.(candidate.place_master_id, candidate.card_key || 'primary')}
                        className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Open card
                      </button>
                    </div>
                  </div>
                );
              })}
              {servicesSplitPass.has_more && (
                <p className="px-4 py-3 text-xs text-amber-800">Showing the first {servicesSplitPass.candidates.length} of {servicesSplitPass.count} matching cards.</p>
              )}
            </div>
          )}
        </div>
      )}

      {active && (
        <div className="border-y border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-amber-900">{progress.message || jobStatusLabel(job)}</span>
            <span className="tabular-nums text-amber-700">{progressPercent}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-amber-200">
            <div className="h-full bg-amber-600 transition-all duration-300" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      )}

      {job?.status === 'failed' && (
        <div className="border-y border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{job.error || 'Report failed'}</div>
      )}

      {!result && !active && job?.status !== 'failed' && (
        <div className="flex h-40 items-center justify-center border-y border-gray-200 text-sm text-gray-500">
          <FileSearch className="mr-2 h-4 w-4" />
          No Librarian report available
        </div>
      )}

      {result && (
        <>
          <div className="grid grid-cols-2 divide-x divide-y divide-gray-200 border-y border-gray-200 bg-white sm:grid-cols-3 lg:grid-cols-6 lg:divide-y-0">
            {[
              ['Inventory', summary.inventory_terms],
              ['Used', summary.used_terms],
              ['Assignments', summary.total_assignments],
              ['Issues', summary.issue_rows],
              ['Aliases', summary.alias_candidates],
              ['Librarian proposals', summary.review_rows],
            ].map(([label, value]) => (
              <div key={label} className="px-4 py-3">
                <p className="text-xs font-medium uppercase text-gray-500">{label}</p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{Number(value || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1" role="group" aria-label="Review filters">
              {FILTERS.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFilter(id)}
                  className={`rounded-md border px-2.5 py-1.5 text-xs font-medium ${
                    filter === id
                      ? 'border-gray-800 bg-gray-800 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <label className="relative block w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search review queue"
                className="h-9 w-full rounded-lg border border-gray-300 bg-white pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </div>

          <TaxonomyReviewTable rows={rows} actions={reviewActions} onOpenPlace={onOpenPlace} />
        </>
      )}

      <AdminJobModal
        isOpen={jobModalOpen}
        title="Taxonomy Librarian report"
        job={starting || runError ? null : job}
        error={runError}
        notice={streamError ? `Live updates reconnecting: ${streamError}` : ''}
        onClose={() => setJobModalOpen(false)}
      >
        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-5 gap-y-3 text-sm sm:grid-cols-3">
              {[
                ['Inventory', summary.inventory_terms],
                ['Assignments', summary.total_assignments],
                ['Issues', summary.issue_rows],
                ['Librarian proposals', summary.review_rows],
                ['Used terms', summary.used_terms],
                ['Aliases', summary.alias_candidates],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="mt-0.5 font-semibold tabular-nums text-gray-900">{Number(value || 0).toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="mb-2 text-xs font-medium uppercase text-gray-500">Downloads</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => downloadJson(result)}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-gray-900 px-3 text-sm font-medium text-white hover:bg-gray-700"
                >
                  <Download className="h-4 w-4" />
                  Full report JSON
                </button>
                {Object.entries(EXPORTS).map(([name, config]) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => downloadExport(name)}
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4" />
                    {config.label} CSV
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </AdminJobModal>
    </div>
  );
}
